import { Injectable } from '@angular/core';
import { ShiftModel } from 'app/models/shift.model';
import { UserModel } from 'app/models/user.model';
import { VehicleModel } from 'app/models/vehicle.model';
import { ApiService } from 'app/shared/apis/api.service';
import { BehaviorSubject, catchError, map, of, switchMap, take } from 'rxjs';
import { DriverSimulationService } from 'app/services/driver-simulation.service';
import { DriverHomeService, DriverHomeState } from './driver-home.service';

export interface DriverContext {
    user: UserModel | null;
    shift: ShiftModel | null;
    home: DriverHomeState | null;
    displayName: string;
    initial: string;
    vehicleCode: string;
    vehicleName: string;
    vehicleMeta: string;
    roleLabel: string;
    partnerLabel: string;
    hasActiveShift: boolean;
}

const EMPTY_CONTEXT: DriverContext = {
    user: null,
    shift: null,
    home: null,
    displayName: '',
    initial: '?',
    vehicleCode: '—',
    vehicleName: 'Viatura não atribuída',
    vehicleMeta: 'Sem turno ativo',
    roleLabel: 'Plantão',
    partnerLabel: '',
    hasActiveShift: false,
};

@Injectable({ providedIn: 'root' })
export class DriverContextService {
    private readonly state$ = new BehaviorSubject<DriverContext>(EMPTY_CONTEXT);

    readonly context$ = this.state$.asObservable();

    constructor(
        private readonly driverHomeService: DriverHomeService,
        private readonly driverSimulation: DriverSimulationService,
    ) {
        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            if (user?.user_id) {
                this.state$.next(this.buildContext(user, null, this.state$.value.home));
            }
        });
        this.refresh();
    }

    get snapshot(): DriverContext {
        return this.state$.value;
    }

    refresh(): void {
        ApiService.userCurrent
            .pipe(
                take(1),
                switchMap((user) => {
                    if (!user?.user_id) {
                        return of(EMPTY_CONTEXT);
                    }

                    const actingUser = this.resolveActingUser(user);

                    return this.driverHomeService.getHome().pipe(
                        map((home) => this.buildContext(actingUser, this.shiftFromHome(home), home)),
                        catchError(() => of(this.buildContext(actingUser, null, null))),
                    );
                }),
            )
            .subscribe((ctx) => this.state$.next(ctx));
    }

    private shiftFromHome(home: DriverHomeState | null): ShiftModel | null {
        if (!home?.shift_id) {
            return null;
        }

        const vehicle: Partial<VehicleModel> | null = home.vehicle_id
            ? {
                  vehicle_id: home.vehicle_id,
                  name: home.vehicle_name ?? undefined,
                  group: home.vehicle_code ?? undefined,
              }
            : null;

        return {
            shift_id: home.shift_id,
            vehicle_id: home.vehicle_id,
            start_at: home.shift_start_at ? new Date(home.shift_start_at) : undefined,
            vehicle: vehicle as VehicleModel,
        } as ShiftModel;
    }

    private resolveActingUser(user: UserModel): UserModel {
        if (!user?.is_admin) {
            return user;
        }
        const label = this.driverSimulation.getSimulatedUserLabel();
        const id = this.driverSimulation.getSimulatedUserId();
        if (!id) {
            return user;
        }
        return {
            ...user,
            user_id: id,
            name: label || user.name,
        } as UserModel;
    }

    private buildContext(user: UserModel | null, shift: ShiftModel | null, home: DriverHomeState | null): DriverContext {
        const displayName = user?.name?.trim() || '';
        const vehicle = shift?.vehicle ?? null;
        const vehicleCode = home?.vehicle_code?.trim() || this.resolveVehicleCode(vehicle);
        const partner = shift?.users?.find((u) => u.user_id !== user?.user_id);
        const partnerLabel = partner?.name ? `dupla com ${this.shortName(partner.name)}` : '';
        const planLabel = vehicle?.vehicle_plan?.name || vehicle?.group || home?.vehicle_name || 'tipo padrão';
        const vehicleMeta = partnerLabel ? `${planLabel} · ${partnerLabel}` : planLabel;
        const hasOperationalContext = home?.is_available !== false || !!home?.vehicle_id || !!home?.active_service_id;
        const hasActiveShift = hasOperationalContext;
        const hasVehicle = !!(home?.vehicle_code?.trim() || home?.vehicle_name?.trim() || (vehicleCode && vehicleCode !== '—'));

        let roleLabel = 'Online';
        if (home?.status === 'IN_SERVICE') {
            roleLabel = 'Em atendimento';
        } else if (home?.status === 'ON_BREAK') {
            roleLabel = 'Em pausa';
        } else if (hasVehicle) {
            const source = home?.vehicle_from_allocation ? 'atendimento' : 'plantão ativo';
            roleLabel = `Viatura ${vehicleCode} · ${source}`;
        } else {
            roleLabel = home?.on_planned_schedule === false ? 'Online · fora da escala' : 'Online · aguardando';
        }

        return {
            user,
            shift,
            home,
            displayName,
            initial: this.initial(displayName),
            vehicleCode: hasVehicle ? vehicleCode : '—',
            vehicleName: home?.vehicle_name || vehicle?.name || 'Viatura não atribuída',
            vehicleMeta: hasActiveShift ? (hasVehicle ? vehicleMeta : 'Sem viatura vinculada') : 'Sem turno ativo',
            roleLabel,
            partnerLabel,
            hasActiveShift,
        };
    }

    private initial(name: string): string {
        const trimmed = name.trim();
        return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
    }

    private shortName(name: string): string {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length <= 1) {
            return parts[0] ?? name;
        }
        return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
    }

    private resolveVehicleCode(vehicle: VehicleModel | null): string {
        if (!vehicle) {
            return '—';
        }
        if (vehicle.group?.trim()) {
            return vehicle.group.trim().toUpperCase();
        }
        const name = vehicle.name?.trim();
        if (!name) {
            return '—';
        }
        const words = name.split(/\s+/).filter(Boolean);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    }
}
